import { Option } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import { MonthsOptions } from "../../../../../../../../../../constants";
import { LoadingCost } from "../../../../../../../../../../models/loading.cost";
import { Location } from "../../../../../../../../../../models/location";
import { Port } from "../../../../../../../../../../models/port";
import { LocationService } from "../../../../../../../../../../services/location.service";
import PortService from "../../../../../../../../../../services/port.service";
import { CategoryForLocalFilter } from "../../../../../../../../../../types";
import {
  BookingModes,
  ContainerModes,
  CronJobType,
} from "../../../../../../../../../../util/enums";
import { simpleSortArray } from "../../../../../../../../../../util/sort";
import { convertValueToSearch } from "../../../../utils";

interface InvoiceFiltersProps {
  data?: LoadingCost[];
}

export const useInvoicesFilters = ({ data }: InvoiceFiltersProps) => {
  const { t: translate } = useTranslation();

  const shipmentCostFilterCategories: CategoryForLocalFilter[] = [
    {
      name: translate("origin"),
      column: "invoiceInvoice.origin",
      onSearch: (value: string) =>
        LocationService.getAll({
          searchByOr: JSON.stringify(convertValueToSearch(value, "suburb")),
          groupBy: JSON.stringify(["location.suburb"]),
          attributes: JSON.stringify(["suburb"]),
        }),
      getOptionLabel: (option: Location) => option?.suburb || "",
      optionColumn: "suburb",
      localKey: "origin",
    },
    {
      name: translate("etaMonth"),
      column: "etaMonth",
      options: MonthsOptions.map((months) => ({ ...months, label: translate(months.label) })),
      optionColumn: "eta",
      getOptionLabel: (value?: string) =>
        MonthsOptions.find((month) => month.value === value)?.label ?? "",
      localKey: "eta",
    },
    {
      name: translate("mode"),
      column: "invoiceInvoice.mode",
      options: [
        { value: BookingModes.SEA, label: translate("sea") },
        { value: BookingModes.AIR, label: translate("air") },
        { value: BookingModes.ROAD, label: translate("road") },
      ],
      getOptionLabel: (option: Option) => option?.label || "",
      optionColumn: "value",
    },
    {
      name: translate("destination"),
      column: "invoiceInvoice.destination",
      onSearch: (value: string) =>
        LocationService.getAll({
          searchByOr: JSON.stringify(convertValueToSearch(value, "suburb")),
          groupBy: JSON.stringify(["location.suburb"]),
          attributes: JSON.stringify(["suburb"]),
        }),
      getOptionLabel: (option: Location) => option?.suburb || "",
      optionColumn: "suburb",
    },
  ];

  const lcrFilterCategories: CategoryForLocalFilter[] = [
    {
      name: translate("origin"),
      column: "origin",
      onSearch: (value: string) =>
        PortService.getAll({
          searchByOr: JSON.stringify(convertValueToSearch(value, "site_code")),
        }),
      optionColumn: "siteCode",
      getOptionLabel: (option: Port) => option?.siteCode || "",
    },
    {
      name: translate("etaMonth"),
      column: "eta",
      options: MonthsOptions.map((months) => ({
        ...months,
        label: translate(months.label),
      })),
      optionColumn: "label",
      getOptionLabel: (value?: string) =>
        MonthsOptions.find((month) => month.value === value)?.label ?? "",
    },
    {
      name: translate("brand"),
      column: "brand",
      options: simpleSortArray(
        Array.from(new Set(data?.map((invoiceRow) => invoiceRow?.brand)) ?? {}) as string[]
      ),
      optionColumn: "none",
      getOptionLabel: (value?: string) =>
        MonthsOptions.find((month) => month.value === value)?.label ?? "",
    },
    {
      name: translate("mode"),
      column: "mode",
      options: [
        { value: ContainerModes.AIR, label: ContainerModes.AIR },
        { value: ContainerModes.FCL, label: ContainerModes.FCL },
        { value: ContainerModes.FCX, label: ContainerModes.FCX },
        { value: ContainerModes.LCL, label: ContainerModes.LCL },
      ],
      optionColumn: "value",
    },
    {
      name: translate("destination"),
      column: "destination",
      onSearch: (value: string) =>
        PortService.getAll({
          searchByOr: JSON.stringify(convertValueToSearch(value, "site_code")),
        }),
      optionColumn: "siteCode",
      getOptionLabel: (option: Port) => option?.siteCode || "",
    },
  ];

  const getInvoiceReportFilters = (type: CronJobType) => {
    switch (type) {
      case CronJobType.LCR_REPORT:
        return lcrFilterCategories;
      case CronJobType.SHIPMENT_COST_REPORT:
        return shipmentCostFilterCategories;
      default:
        return lcrFilterCategories;
    }
  };

  return { getInvoiceReportFilters };
};
